import { cn } from "../../utils/classnames.js";
import { Button } from "../atoms/button.js";
import { Drawer, DrawerFooter, DrawerHeader, DrawerTrigger } from "../atoms/drawer.js";
import { useCurrentBreakpoint } from "../../hooks/breakpoints.js";
import { useArrowKeyNavigation } from "../../hooks/keyboard.js";
import { useLayoutContext } from "../../hooks/layout-context.js";
import { LinkButton } from "../atoms/link-button.js";
import { DrawerPanel } from "../molecules/drawer-panel.js";
import { DrawerPopup } from "../molecules/drawer-popup.js";
import { ThemeToggle } from "../molecules/theme-toggle.js";
import { useCallback, useMemo, useRef, useState } from "react";
import { isEmptyish } from "remeda";
import { jsx, jsxs } from "react/jsx-runtime";
import { Loader, Menu } from "lucide-react";
import { useLingui } from "@lingui/react";
import { useIsClient } from "@bitcart/hooks";
import { MOBILE_MENU_CONTENT_TESTID, MOBILE_MENU_TOGGLE_TESTID, UI_THEME_MOBILE_TOGGLE_TESTID } from "@bitcart/qa";
//#region src/components/organisms/website-mobile-menu.tsx
const DEFAULT_TRIGGER_RENDER = /* @__PURE__ */ jsx(Button, {
	size: "icon-lg",
	variant: "ghost"
});
const WebsiteMobileMenu = ({ layoutControls, triggerIcon: customTriggerIcon, triggerRender = DEFAULT_TRIGGER_RENDER, classNames }) => {
	const { _: $__ } = useLingui();
	const { Link, currentRoute, layoutConfig: { brand, project, navigation: { rootRoutePathname } }, primaryNavCatalog: { iconLinks, labeledLinks } } = useLayoutContext();
	const isClient = useIsClient();
	const currentBreakpoint = useCurrentBreakpoint();
	const popupRef = useRef(null);
	const labeledNavRef = useArrowKeyNavigation({
		prevKey: "ArrowUp",
		nextKey: "ArrowDown"
	});
	const iconNavRef = useArrowKeyNavigation({
		prevKey: "ArrowLeft",
		nextKey: "ArrowRight"
	});
	const [isOpen, setIsOpen] = useState(false);
	const toggle = useCallback(() => setIsOpen((state) => !state), []);
	const close = useCallback(() => setIsOpen(false), []);
	const getInitialFocusElement = useCallback(() => popupRef.current?.querySelector("[aria-current='page']") ?? popupRef.current?.querySelectorAll("a, button").item(1) ?? null, []);
	const triggerIcon = useMemo(() => {
		if (isClient) return customTriggerIcon ?? /* @__PURE__ */ jsx(Menu, {});
		else return /* @__PURE__ */ jsx(Loader, { className: "animate-spin text-foreground" });
	}, [customTriggerIcon, isClient]);
	return /* @__PURE__ */ jsxs(Drawer, {
		open: isOpen && currentBreakpoint === "sm",
		onOpenChange: setIsOpen,
		children: [/* @__PURE__ */ jsx(DrawerTrigger, {
			disabled: !isClient,
			onClick: toggle,
			render: triggerRender,
			className: classNames?.trigger,
			"aria-label": isOpen ? $__({
				id: "vcpc5o",
				message: "Close menu"
			}) : $__({
				id: "GSr0rF",
				message: "Open menu"
			}),
			"data-testid": MOBILE_MENU_TOGGLE_TESTID,
			children: triggerIcon
		}), /* @__PURE__ */ jsxs(DrawerPopup, {
			ref: popupRef,
			initialFocus: getInitialFocusElement,
			keepMounted: currentBreakpoint === "sm",
			className: classNames?.popup,
			"aria-label": $__({
				id: "4q1KwJ",
				message: "Main Menu"
			}),
			children: [
				/* @__PURE__ */ jsx(DrawerHeader, {
					className: "flex-row items-center justify-center",
					children: /* @__PURE__ */ jsxs(Link, {
						href: rootRoutePathname,
						onClick: close,
						className: cn(`
              focus-visible:outline-ring/90
              gap-3 rounded-lg p-1 px-2
              focus-visible:text-primary-text
              hover:text-primary-text
              flex w-fit items-center
              focus-visible:outline-3
            `),
						children: [/* @__PURE__ */ jsx("div", {
							className: "size-10 flex items-center justify-center",
							children: /* @__PURE__ */ jsx("img", {
								alt: "",
								src: project?.logoImageSrc ?? brand.logoImageSrc,
								className: "size-10"
							})
						}), /* @__PURE__ */ jsx("span", {
							className: "text-2xl font-bold",
							children: project?.canonicalName ?? brand.name
						})]
					})
				}),
				/* @__PURE__ */ jsx(DrawerPanel, {
					scrollFade: false,
					"data-testid": MOBILE_MENU_CONTENT_TESTID,
					children: /* @__PURE__ */ jsx("nav", {
						ref: labeledNavRef,
						className: "gap-2 flex w-full flex-col",
						"aria-label": $__({
							id: "UxKoFf",
							message: "Navigation"
						}),
						children: labeledLinks.map((link) => {
							const isActive = !link.isExternal && link.href === currentRoute.pathnameWithHash;
							return /* @__PURE__ */ jsx(LinkButton, {
								onClick: close,
								variant: "ghost",
								className: cn("w-full justify-start text-left", { "bg-accent text-accent-foreground": isActive }),
								"aria-current": isActive ? "page" : void 0,
								"aria-label": link.hint ?? link.label,
								href: link.href,
								children: link.label
							}, link.label + link.href);
						})
					})
				}),
				/* @__PURE__ */ jsx(DrawerFooter, {
					className: "bg-transparent",
					children: /* @__PURE__ */ jsxs("div", {
						className: cn("gap-4 flex w-full items-center", {
							"justify-end": isEmptyish(iconLinks),
							"justify-between": !isEmptyish(iconLinks)
						}),
						children: [
							!isEmptyish(iconLinks) && /* @__PURE__ */ jsx("nav", {
								ref: iconNavRef,
								className: "gap-4 flex",
								"aria-label": $__({
									id: "-4cugt",
									message: "Other links"
								}),
								children: iconLinks?.map(({ icon: Icon, ...link }) => {
									return /* @__PURE__ */ jsx(LinkButton, {
										onClick: close,
										title: link.hint,
										size: "icon-lg",
										variant: "ghost",
										className: "hover:text-primary-text bg-muted/25 dark:hover:bg-accent/30",
										"aria-label": link.hint,
										href: link.href,
										children: /* @__PURE__ */ jsx(Icon, { "aria-hidden": "true" })
									}, link.hint + link.href);
								})
							}),
							layoutControls,
							/* @__PURE__ */ jsx(ThemeToggle, {
								testId: UI_THEME_MOBILE_TOGGLE_TESTID,
								className: cn(`size-10 hover:text-primary-text bg-muted/25 dark:hover:bg-accent/30`)
							})
						]
					})
				})
			]
		})]
	});
};
//#endregion
export { WebsiteMobileMenu };

//# sourceMappingURL=website-mobile-menu.js.map